// =============================================
// useAutoGenerateBulk — stan bulk auto-generate map (STOP-E)
// =============================================
// Wzorzec z useStandaloneBulk: refs pause/stop, state running/progress/summary,
// beforeunload guard, fire-and-forget start.
//
// KOREKTA 6.4 — UWAGA: ten hook trzyma state w komponencie. Persisten cross-nav
// wymagac bedzie Context Provider w STOP-F. Tu na razie standalone hook (do testow
// w dev console + jako base dla Provider).
//
// KOREKTA 6.5 — workspace switch lock: w start() PRZED runBulkAutoGenerate sync-set
// localStorage['bulk_auto_running'] = 'true'. W finally sync-clear. Plus useEffect
// na `running` zaleznosc dla belt-and-suspenders.
// =============================================

import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import {
  runBulkAutoGenerate,
  type BulkAutoGenerateOptions,
  type BulkControl,
  type BulkProgressEvent,
  type BulkSummary,
} from "@/services/autoGenerate/autoGenerateBulkRunner";

/**
 * Synchronicznie sprawdza localStorage flag czy bulk auto-generate biegnie.
 * Uzywane przez `useWorkspace.setWorkspace` (KOREKTA 6.5) — workspace switch
 * lock POZA React lifecycle.
 *
 * SSR-safe: gdy window undefined zwraca false.
 */
export function isAutoGenerateBulkRunning(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem("bulk_auto_running") === "true";
  } catch {
    return false;
  }
}

function setBulkRunningFlag(running: boolean): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      "bulk_auto_running",
      running ? "true" : "false",
    );
  } catch {
    // localStorage moze byc zablokowany (private mode) — silent.
  }
}

export interface AutoGenerateBulkProgress {
  total: number;
  done: number;
  failed: number;
  currentMapId?: string;
  currentStage?: string;
  /** Ile stage'ow w current mapie juz zaliczonych (done/skipped) — sub-progress. */
  currentMapStagesCompleted: number;
  /** Ile stage'ow do wykonania w current mapie (z opcji targetStage). */
  currentMapStagesTotal: number;
  /** Phase: idle/running/paused/stopping/done. */
  phase: "idle" | "running" | "paused" | "stopping" | "done";
  /** Ostatnia tresc public log line (dla UI tooltips). */
  lastMsg?: string;
  /** Ile lockow zwolniono w pre-run cleanup. */
  cleanedLocks?: number;
  /** Timestamp ostatniej aktywnosci (ms). */
  lastActivityTs: number;
}

// Stages z targetStage — odpowiadaja STAGE_ORDER w autoGenerateMapRunner.
// Bulk runner moze tez wykonac pre-stage SBD (gdy boundaries puste) — to bonus
// poza listy, nie liczone w sub-progress (zbyt szybkie zeby ruszyc pasek).
const STAGES_FOR_TARGET: Record<string, number> = {
  seeds: 1,
  nodes: 2,
  serp: 3,
  entities: 4,
  eav: 5,
  relations: 6,
};

const INITIAL_PROGRESS: AutoGenerateBulkProgress = {
  total: 0,
  done: 0,
  failed: 0,
  currentMapStagesCompleted: 0,
  currentMapStagesTotal: 6,
  phase: "idle",
  lastActivityTs: 0,
};

export function useAutoGenerateBulk() {
  const { user } = useAuth();
  const [running, setRunning] = useState(false);
  const [progress, setProgress] =
    useState<AutoGenerateBulkProgress>(INITIAL_PROGRESS);
  const [summary, setSummary] = useState<BulkSummary | null>(null);

  // Refs czytane przez bulk runner control (closure-d)
  const pauseRequestedRef = useRef<boolean>(false);
  const stopRequestedRef = useRef<boolean>(false);

  // beforeunload guard — ostrzezenie przed zamknieciem karty podczas bulka
  useEffect(() => {
    if (!running) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue =
        "Trwa bulk auto-generate map — zamknac karte?";
      return e.returnValue;
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [running]);

  // Belt-and-suspenders dla KOREKTY 6.5 — sync set/clear w start()/finally
  // pokrywa happy path, ten useEffect lapie unmount / crashed nav.
  useEffect(() => {
    setBulkRunningFlag(running);
    return () => {
      // Cleanup gdy hook unmount podczas running — zostawia jednak start()
      // sync set z localStorage, wiec teoretycznie zostaje 'true'. UI Provider
      // w STOP-F bedzie miec staly mount.
      // NIE clear tu — uzytkownik moze wrocic do strony i kontynuowac.
    };
  }, [running]);

  /**
   * Uruchom partie. Fire-and-forget. Status sledz przez `running` + `progress`.
   * Po zakonczeniu `summary` dostaje pelen raport.
   */
  const start = useCallback(
    async (mapIds: string[], options: BulkAutoGenerateOptions): Promise<void> => {
      if (!mapIds.length) return;
      if (!user) {
        console.warn("[useAutoGenerateBulk] start without auth user");
        return;
      }

      // KOREKTA 6.5: sync set PRZED runBulkAutoGenerate (zamyka race window)
      setBulkRunningFlag(true);

      setRunning(true);
      pauseRequestedRef.current = false;
      stopRequestedRef.current = false;
      setSummary(null);
      setProgress({
        ...INITIAL_PROGRESS,
        total: mapIds.length,
        currentMapStagesTotal:
          STAGES_FOR_TARGET[options.targetStage] || 6,
        phase: "running",
        lastActivityTs: Date.now(),
      });

      const control: BulkControl = {
        pauseRef: pauseRequestedRef,
        stopRef: stopRequestedRef,
      };

      try {
        const result = await runBulkAutoGenerate(
          mapIds,
          user.id,
          options,
          control,
          (event: BulkProgressEvent) => {
            setProgress((prev) => {
              const next: AutoGenerateBulkProgress = {
                ...prev,
                lastActivityTs: Date.now(),
              };
              if (event.type === "bulk_start") {
                next.phase = "running";
                if (event.total != null) next.total = event.total;
              } else if (event.type === "cleanup_done") {
                next.cleanedLocks = event.cleanedLocks;
              } else if (event.type === "map_start") {
                next.currentMapId = event.currentMapId;
                next.currentStage = undefined;
                next.currentMapStagesCompleted = 0; // reset sub-progress dla nowej mapy
                if (event.done != null) next.done = event.done;
              } else if (event.type === "map_stage") {
                next.currentMapId = event.currentMapId;
                if (event.stageEvent) {
                  next.currentStage = event.stageEvent.stage;
                  // Stage done/skipped -> increment sub-progress (pasek rusza
                  // WEWNATRZ obecnej mapy, nie tylko gdy cala mapa skonczy)
                  if (
                    event.stageEvent.type === "stage_done" ||
                    event.stageEvent.type === "stage_skipped"
                  ) {
                    next.currentMapStagesCompleted = Math.min(
                      next.currentMapStagesCompleted + 1,
                      next.currentMapStagesTotal,
                    );
                  }
                  if (event.stageEvent.msg) {
                    next.lastMsg = event.stageEvent.msg;
                  }
                }
              } else if (event.type === "map_done") {
                if (event.done != null) next.done = event.done;
                if (event.failed != null) next.failed = event.failed;
                next.currentMapId = undefined;
                next.currentStage = undefined;
                next.currentMapStagesCompleted = 0;
              } else if (event.type === "paused") {
                next.phase = "paused";
              } else if (event.type === "stopping") {
                next.phase = "stopping";
                if (event.done != null) next.done = event.done;
              } else if (event.type === "bulk_done") {
                next.phase = "done";
                if (event.done != null) next.done = event.done;
                if (event.failed != null) next.failed = event.failed;
                next.currentMapId = undefined;
                next.currentStage = undefined;
              }
              return next;
            });
          },
        );
        setSummary(result);
      } finally {
        setRunning(false);
        pauseRequestedRef.current = false;
        stopRequestedRef.current = false;
        // KOREKTA 6.5: sync clear
        setBulkRunningFlag(false);
      }
    },
    [user],
  );

  const pause = useCallback(() => {
    pauseRequestedRef.current = true;
    setProgress((p) => ({ ...p, phase: "paused" }));
  }, []);

  const resume = useCallback(() => {
    pauseRequestedRef.current = false;
    setProgress((p) => ({ ...p, phase: "running" }));
  }, []);

  const stop = useCallback(() => {
    stopRequestedRef.current = true;
    setProgress((p) => ({ ...p, phase: "stopping" }));
  }, []);

  return {
    running,
    progress,
    summary,
    start,
    pause,
    resume,
    stop,
  };
}
